import { useState, useRef, useMemo, useCallback } from "react";
import { useIntl } from "react-intl";
import { useSelector, useDispatch } from "react-redux";
//Components
import DataViewInfiniteScroll from 'Components/DataViewInfiniteScroll';
import { Button } from "@gpstrackit/react-controls";

//Views
import CreateEditKeyFob from 'Modules/keysFobs/.components/createEditKeyFob';
import DeleteKeyFob from 'Modules/keysFobs/.components/deleteKeyFob';
import MoveToAnotherAccount from 'Modules/keysFobs/.components/moveToAnotherAccount'
import UnassignDrivers from 'Modules/keysFobs/.components/unassignDrivers'
import BulkAddDriverIDTags from 'Modules/keysFobs/.components/bulkAddDriverIdTags'

//Notification
import { NotificationManager } from "Components/Notifications";

//Actions
import {
    keyfobsCreateEditOpenModal,
    keyfobsDeleteOpenModal,
    bulkAddDriverIdTagsOpenModal,
    moveToAnotherAccountOpenModal,
    unassignDriversOpenModal,
} from 'Redux/actions';

//Permission
import { HasPermission } from "Core/security";

const KeyFobsTab = (props) => {
    const { companyId, tab, height, company } = props;
    const { messages } = useIntl();
    const tableRef = useRef();
    const dispatch = useDispatch();

    const openModalCreateEdit = useSelector(state => state.createEditKeyFobRedux.openModal);
    const openModalDelete = useSelector(state => state.deleteKeyFobRedux.openModal);
    const openModalBulkAddDriverIdTags = useSelector(state => state.bulkAddDriverIdTagsRedux.openModal);
    const openModalMoveToAnotherAccount = useSelector(state => state.moveToAnotherAccountRedux.openModal);
    const openModalUnassignDrivers = useSelector(state => state.unassignDriversRedux.openModal);
    const user = useSelector(state => state.securityRedux.user);

    const [columns, setColumns] = useState([
        {
            key: "hex",
            field: "hex",
            title: messages['keysfobs_tab_hex'],
            show: true,
            resize: true,
            width: 250,
            sort: true,
            export: true,
            code: "keysfobs_tab_hex",
            functionString: 'return row?.hex',
            render: (item) => {
                return <span>{item?.hex}</span>
            }
        },
        {
            key: "keyFobModel_name",
            field: "keyFobModel { id name }",
            title: messages['keysfobs_tab_model'],
            show: true,
            sort: true,
            width: 300,
            export: true,
            functionString: 'return row?.keyFobModel?.name',
            code: "keysfobs_tab_model",
            render: (item) => {
                return (<span>{item.keyFobModel?.name}</span>)
            }
        },
        {
            key: "driver_name",
            field: "driver { id name }",
            title: messages['keysfobs_tab_driver'],
            show: true,
            resize: true,
            sort: true,
            export: true,
            functionString: 'return row?.driver?.name',
            code: "keysfobs_tab_driver",
            render: (item) => {
                return <span>{item?.driver?.name}</span>
            }
        }
    ])
    const [searchFields, setSearchFields] = useState(["hex", "keyFobModel_name", "driver_name"]);

    // GraphQL configuration
    const graphqlConfig = useMemo(() => ({
        function: "searchKeyFobs",
        sortType: "KeyFobSortInput",
        conditionsType: "KeyFobSearchInput",
        customQuery: "id",
        dynamicQuery: true,
    }), []);

    // Search configuration
    const searchConfig = useMemo(() => ({
        conditions: ["hex", "driver_name"],
        optionValue: "id",
        optionLabel: "hex",
        custom: { fields: ["companyId"], terms: [companyId], exact: true },
    }), [companyId]);

    // Export configuration
    const exportConfig = useMemo(() => ({
        fileName: "keyfobs",
    }), []);

    // UI configuration
    const uiConfig = useMemo(() => ({
        hideAllInPagination: true,
        hideSplit: true,
        hideFilters: true,
        topHeight: props?.single ? 286 : 200,
        height: height && height + "px" || "500px",
    }), [height]);

    // Options for row actions
    const options = useMemo(() => [
        {
            id: "edit-keyfobs",
            label: messages['edit'],
            show: true,
            single: true,
            disabled: !HasPermission(user, "kfb", "u"),
            action: (items) => {
                let currentKey = items[0]?.id
                currentKey && dispatch(keyfobsCreateEditOpenModal(currentKey));
            }
        },
        {
            id: "move-keyfobs",
            label: messages['keyfobs_main_move'],
            show: true,
            disabled: !HasPermission(user, "kfb", "u"),
            action: (items) => {
                dispatch(moveToAnotherAccountOpenModal(items));
            }
        },
        {
            id: "unassign-keyfobs",
            label: messages['keyfobs_main_unassign'],
            show: true,
            disabled: !HasPermission(user, "kfb", "u"),
            action: (items) => {
                dispatch(unassignDriversOpenModal(items));
            }
        },
        {
            id: "delete-keyfobs",
            label: messages['delete'],
            show: true,
            single: true,
            disabled: !HasPermission(user, "kfb", "d"),
            action: (items) => {
                let currentKey = items[0]
                dispatch(keyfobsDeleteOpenModal(currentKey))
            }
        }
    ], [messages, user, dispatch]);

    // Menu options
    const menuOptions = useCallback(() => {
        return [
            messages['edit'],
            messages['delete'],
        ]
    }, [messages]);

    // Menu click handler
    const handleClickMenu = useCallback((event, item) => {
        if (event === messages['edit']) {
            if (HasPermission(user, "kfb", "u")) {
                dispatch(keyfobsCreateEditOpenModal(item?.id));
            } else {
                NotificationManager.warning(
                    messages['common_error_permission'],
                    messages['edit'],
                    5000,
                );
            }
        }
        if (event === messages['delete']) {
            if (HasPermission(user, "kfb", "d")) {
                dispatch(keyfobsDeleteOpenModal(item));
            } else {
                NotificationManager.warning(
                    messages['common_error_permission'],
                    messages['delete'],
                    5000,
                );
            }
        }
    }, [messages, user, dispatch]);

    // Extra button
    const extraButton = useCallback(() => {
        return (
            <div style={{ margin: '0 5px' }}>
                <Button
                    className="btn-dark-blue"
                    disabled={!HasPermission(user, "kfb", "c")}
                    onClick={(e) => {
                        e.preventDefault();
                        dispatch(bulkAddDriverIdTagsOpenModal());
                    }}
                >
                    {messages['keyfobs_main_add_driver_key']}
                </Button>
            </div>
        )
    }, [user, dispatch, messages]);

    // Condition change handler
    const handleChangeConditions = useCallback((tags, conditions, total, newColumns, rows, sort) => {
        let currentConditions = {}
        let currentTags = {}
        if (!conditions && tags) {
            currentConditions = tableRef?.current?.conditions;
            currentTags = tags;
        } else {
            currentConditions = conditions || [];
            currentTags = currentConditions?.filter((x) => x?.tags)[0]?.tags || [];
            if (currentTags && currentTags.length > 0) tableRef?.current?.setTags(currentTags);
        }

        currentConditions = currentConditions?.filter((item) => item?.fields[0] !== 'id' && item?.fields[0] !== 'hex' && item?.fields[0] !== 'companyId');

        let tagsById = currentTags.filter((item) => item.id !== item.hex);
        if (tagsById?.length > 0) {
            //New Items
            let currentIds = tagsById.map((item) => (item.id));
            if (currentIds.length > 0) {
                currentConditions.push({ fields: ["id"], terms: currentIds, exact: true, tags: currentTags, conditionOperator: "OR" });
            }
        }
        let tagsByLabel = currentTags.filter((item) => item.id === item.hex);
        if (tagsByLabel.length > 0) {
            let currentLabels = tagsByLabel.map((item) => (item.hex))
            if (currentLabels.length > 0) {
                currentConditions.push({ fields: searchFields, terms: currentLabels, exact: false, tags: currentTags, conditionOperator: "OR" })
            }
        }

        currentConditions.push({ fields: ["companyId"], terms: [companyId], exact: true, conditionOperator: "AND" });

        if (!conditions && tags) tableRef?.current?.getData({ newConditions: currentConditions });
        else tableRef?.current?.getData({ newRows: rows, newSort: sort, newConditions: currentConditions });
    }, [companyId, searchFields]);

    return (
        <div className="keyfobs-tab" style={{ marginTop: 10 }}>
            <DataViewInfiniteScroll
                ref={tableRef}
                columns={columns}
                name={`pm-keyfobs-tab-${companyId}`}
                entity="keyfobs-tab-account"
                infiniteScroll={false}
                selectionConfig={{ showCounter: false }}
                graphqlConfig={graphqlConfig}
                searchConfig={searchConfig}
                exportConfig={exportConfig}
                uiConfig={uiConfig}
                defaultsConfig={{
                    rowsPerPage: 25,
                    valuesPerPage: [10, 25, 50]
                }}
                asyncSelect
                onChangeConditions={handleChangeConditions}
                searchFields={searchFields}
                onChangeColumns={(items) => setColumns(items)}
                tableButtons={['orderColumn', 'export']}
                options={options}
                menuOptions={menuOptions}
                onClickMenu={handleClickMenu}
                extraButton={extraButton}
            />
            {openModalCreateEdit && <CreateEditKeyFob tableRef={tableRef} />}
            {openModalDelete && <DeleteKeyFob tableRef={tableRef} />}
            {openModalBulkAddDriverIdTags && <BulkAddDriverIDTags account={company} principalTableRef={tableRef} fromAccount={true} />}
            {openModalMoveToAnotherAccount && <MoveToAnotherAccount tableRef={tableRef} />}
            {openModalUnassignDrivers && <UnassignDrivers tableRef={tableRef} />}
        </div>
    )
}
export default KeyFobsTab;